import kebabCase from 'lodash/kebabCase';
import { MouseEvent } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getSupportedAgeGroups } from '@uplift/api';
import { useIsMobile } from '@uplift/hooks';
import { AgeGroupEnum } from '@uplift/types';

import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepTipPopover,
  StepToggleButton, StepToggleButtonGroup, Tip
} from '../../components';
import { AgeNextData } from '../types';

export type AgeProps = {
  onNext: (data: AgeNextData) => void;
  onBack: () => void;
  isBookingForSelf: boolean;
  banner?: React.ReactNode;
};

const ageLabel: Record<AgeGroupEnum, string> = {
  [AgeGroupEnum.children_or_teens]: '17 or younger',
  [AgeGroupEnum.young_adults]: '18–25',
  [AgeGroupEnum.adults]: '26–64',
  [AgeGroupEnum.elderly]: '65+'
};

const tipTitle = 'Why are we asking this?';
const tipDescription = (isBookingForSelf: boolean) => `Some providers specialize in working with certain ages. This helps us recommend the right provider${isBookingForSelf ? ' for you' : ''}.`;

export const Age: React.VFC<AgeProps> = props => {
  const isMobile = useIsMobile();
  const { data: ages = [] } = useQuery({
    queryKey: [ 'supported-ages' ],
    queryFn: async () => {
      const result = await getSupportedAgeGroups();

      return result.ages;
    }
  });

  const onChange = (_event: MouseEvent<HTMLElement>, ageId: string) => {
    const age = ages.find(age => age.id === ageId);

    if (!age) {
      return;
    }

    props.onNext({ age: { id: age.id, ageGroup: age.ageGroup } });
  };

  return (
    <StepContainer>
      {props.banner}
      <StepContent>
        <StepHeading>{props.isBookingForSelf ? 'How old are you?' : 'How old is the person you are booking for?'}</StepHeading>
        <StepForm>
          <Box display="grid" gridTemplateColumns="343px 1fr" gap="1rem" alignItems="start">
            <StepToggleButtonGroup onChange={onChange}>
              {ages.map(({ ageGroup, id }) => (
                <StepToggleButton key={ageGroup} value={id} data-testid={`age-${kebabCase(ageLabel[ageGroup])}`}>
                  {ageLabel[ageGroup]}
                </StepToggleButton>
              ))}
            </StepToggleButtonGroup>
            {!isMobile && (
              <Tip
                title={tipTitle}
                description={tipDescription(props.isBookingForSelf)}
              />
            )}
          </Box>
        </StepForm>
      </StepContent>
      {isMobile && (
        <StepTipPopover title={tipTitle}>
          {tipDescription(props.isBookingForSelf)}
        </StepTipPopover>
      )}
      <StepNavigator onBack={props.onBack} desktopOnly />
    </StepContainer>
  );
};
